'use client';

import { useCookieConsent } from '@/app/contexts/cookie-consent-context';
import { Box, Button, Container, Paper, Stack, styled, Typography } from '@mui/material';

export default function ConsentBanner() {
  const { 
    setConsent, 
    showConsentBanner, 
    setShowConsentBanner,
    setShowConsentManager 
  } = useCookieConsent();

  if (!showConsentBanner) {
    return null;
  }

  // TODO: Redundant functionality with ConsentManager's handleAcceptAll
  // Both methods set the same consent state. Consider extracting this to a shared utility function
  // that can be imported by both ui to ensure consistent behavior.
  const handleAcceptAll = () => {
    setConsent({
      necessary: true,
      analytics: true,
      externalMedia: true,
    });
    setShowConsentBanner(false);
  };

  const handleAcceptNecessary = () => {
    setConsent({
      necessary: true,
      analytics: false,
      externalMedia: false,
    });
    setShowConsentBanner(false);
  };

  const handleCustomize = () => {
    setShowConsentBanner(false);
    setShowConsentManager(true);
  };

  return (
    <BannerRoot>
      <Container>
        <ContentWrapper>
          <TextContainer>
            <BannerTitle as="h2" variant="h6">Cookie-Einwilligung</BannerTitle>
            <BannerText variant="body1">
              Wir verwenden Cookies, um Ihr Nutzungserlebnis zu verbessern, personalisierte Werbung oder Inhalte bereitzustellen und unseren Datenverkehr zu analysieren.
              Wenn Sie auf „Alle akzeptieren“ klicken, stimmen Sie der Verwendung von Cookies zu. Einige Funktionen wie Karten benötigen bestimmte Cookies, um ordnungsgemäß zu funktionieren.
            </BannerText>
          </TextContainer>
          <ButtonGroup>
            <SecondaryButton onClick={handleCustomize}>
              Einstellungen
            </SecondaryButton>
            <SecondaryButton onClick={handleAcceptNecessary}>
              Nur notwendige akzeptieren
            </SecondaryButton>
            <PrimaryButton onClick={handleAcceptAll}>
              Alle akzeptieren
            </PrimaryButton>
          </ButtonGroup>
        </ContentWrapper>
      </Container>
    </BannerRoot>
  );
}

const BannerRoot = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 50,
  padding: theme.spacing(2),
  boxShadow: theme.shadows[3],
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: 'var(--background)',
  color: 'var(--foreground)',
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

const TextContainer = styled(Box)({
  flex: 1,
});

const BannerTitle = styled(Typography)({
  marginBottom: 8,
  fontWeight: 600,
  color: 'var(--foreground)',
});

const BannerText = styled(Typography)({
  marginBottom: 16,
  color: 'var(--foreground)',
});

const ButtonGroup = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  fontSize: '0.875rem',
  fontWeight: 500,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'var(--secondary)',
  color: 'var(--foreground)',
  border: '1px solid var(--foreground)',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  fontSize: '0.875rem',
  fontWeight: 500,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'var(--primary)',
  color: theme.palette.primary.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));
